import React, { useContext, useEffect } from 'react';
import { MediaContext } from '@volkswagen-onehub/components-core';
import { useTrackingService } from '@/contexts/TrackingServiceContext';
import { TrackingEvent, Video as VideoTrackingData } from '../../util/tracking/definitions';

export const formatTimeGroup = (time: number): string =>
  time < 10 ? `0${time.toFixed(0)}` : time.toFixed(0);

export const formatTime = (timeInSeconds: number): string => {
  let time = Math.round(timeInSeconds);
  const hours = Math.floor(time / 3600);
  time -= hours * 3600;
  const minutes = Math.floor(time / 60);
  const seconds = time - minutes * 60;

  return `${formatTimeGroup(hours)}:${formatTimeGroup(minutes)}:${formatTimeGroup(seconds)}`;
};

export const VideoTracker: React.FunctionComponent = () => {
  const mediaElement = useContext(MediaContext);
  const trackingService = useTrackingService();

  useEffect(() => {
    if (!mediaElement) {
      return undefined;
    }

    const video: VideoTrackingData = {
      title: mediaElement.title || undefined,
      id: mediaElement.id || undefined,
      platform: 'AEM',
      url: mediaElement.src,
      length: undefined,
    };

    const handleLoadedMetadata = () => {
      if (mediaElement.duration) {
        video.length = formatTime(mediaElement.duration);
      }
    };

    let isInitialPlay = true;
    const handlePlay = () => {
      if (isInitialPlay) {
        trackingService.track(TrackingEvent.VideoPlayerVideoStart, {
          video,
          eventInfo: { contentId: `started cameraflight video` },
        });
      } else {
        trackingService.track(TrackingEvent.VideoPlayerVideoPlayClick, {
          video: { ...video, timeplayed: formatTime(mediaElement.currentTime) },
          eventInfo: { contentId: `play cameraflight video` },
        });
      }
    };

    const handlePause = () => {
      isInitialPlay = false;
      if (!mediaElement.ended) {
        trackingService.track(TrackingEvent.VideoPlayerVideoPauseClick, {
          video: { ...video, timeplayed: formatTime(mediaElement.currentTime) },
          eventInfo: { contentId: `paused cameraflight video` },
        });
      }
    };

    let nextMilestone = 25;
    const handleTimeUpdate = () => {
      const percentageViewed = (mediaElement.currentTime / mediaElement.duration) * 100;

      const milestone = Math.floor(percentageViewed / 25) * 25;
      if (milestone > 0 && nextMilestone <= milestone) {
        trackingService.track(TrackingEvent.VideoPlayerVideoMilestoneReached, {
          video: { ...video, timeplayed: formatTime(mediaElement.currentTime), milestone },
          eventInfo: { contentId: `reached milestone cameraflight video` },
        });
        nextMilestone += 25;
      }
    };

    const handleFullScreenChange = () => {
      const isFullScreen = document.fullscreenElement === mediaElement.parentElement;

      trackingService.track(
        isFullScreen
          ? TrackingEvent.VideoPlayerFullscreenOpen
          : TrackingEvent.VideoPlayerFullscreenClose,
        {
          video: { ...video, timeplayed: formatTime(mediaElement.currentTime) },
          eventInfo: {
            contentId: isFullScreen
              ? `opened fullscreen cameraflight video`
              : `closed fullscreen cameraflight video`,
          },
        },
      );
    };

    mediaElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    mediaElement.addEventListener('play', handlePlay);
    mediaElement.addEventListener('pause', handlePause);
    mediaElement.addEventListener('timeupdate', handleTimeUpdate);
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);

    return () => {
      mediaElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      mediaElement.removeEventListener('play', handlePlay);
      mediaElement.removeEventListener('pause', handlePause);
      mediaElement.removeEventListener('timeupdate', handleTimeUpdate);
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
    };
  });

  return <></>;
};
